const TTL = 2 * 60 * 1000; // 2 minutes

interface CacheEntry {
    data: unknown;
    ts: number;
}

const store = new Map<string, CacheEntry>();

export function getCache(key: string): unknown | null {
    const entry = store.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > TTL) {
        store.delete(key);
        return null;
    }
    return entry.data;
}

export function setCache(key: string, data: unknown): void {
    store.set(key, { data, ts: Date.now() });
}

/** Clears the entire cache — call on logout to prevent data leaking between sessions. */
export function clearCache(): void {
    store.clear();
}

/** Clears all cache entries whose key starts with any of the given prefixes. */
export function invalidateCache(...prefixes: string[]): void {
    for (const prefix of prefixes) {
        for (const key of store.keys()) {
            if (key === prefix || key.startsWith(prefix + "/") || key.startsWith(prefix + "?")) {
                store.delete(key);
            }
        }
    }
}

const pendingGets = new Map<string, Promise<unknown>>();

/** In-flight GET for this URL, if one is already underway. */
export function getPendingGet(key: string): Promise<unknown> | null {
    return pendingGets.get(key) ?? null;
}

/**
 * Registers `promise` as the in-flight GET for `key` so concurrent callers
 * (e.g. two components mounting in the same tick, each wanting the same
 * endpoint) share one network request instead of each firing their own.
 * Self-clears once the request settles, independent of its outcome.
 */
export function setPendingGet(key: string, promise: Promise<unknown>): void {
    pendingGets.set(key, promise);
    promise.finally(() => {
        if (pendingGets.get(key) === promise) pendingGets.delete(key);
    });
}

/**
 * Derives which cache prefixes to invalidate based on the mutated URL.
 * Called automatically by apiRequest after non-GET requests.
 */
export function invalidateForMutation(url: string): void {
    // Strip /api prefix so matching works regardless of how the URL was constructed
    const path = url.startsWith("/api") ? url.slice(4) : url;

    let prefixes: string[] = [];
    if (path.startsWith("/patients")) {
        prefixes = ["/api/patients", "/api/recipients", "/api/invoices"];
    } else if (path.startsWith("/recipients")) {
        prefixes = ["/api/recipients", "/api/patients", "/api/invoices"];
    } else if (path.startsWith("/invoices/manual")) {
        // A new invoice changes the patients' invoice counts and the dashboard too, and removes its draft.
        prefixes = ["/api/invoices", "/api/charges", "/api/patients", "/api/recipients", "/api/dashboard", "/api/invoice-drafts"];
    } else if (path.startsWith("/invoice-drafts")) {
        prefixes = ["/api/invoice-drafts"];
    } else if (path.startsWith("/invoice-templates")) {
        prefixes = ["/api/invoice-templates"];
    } else if (path.startsWith("/justifications")) {
        prefixes = ["/api/justifications"];
    } else if (path.startsWith("/invoices")) {
        prefixes = ["/api/invoices", "/api/charges"];
    } else if (path.startsWith("/exams")) {
        prefixes = ["/api/exams", "/api/invoices", "/api/goa", "/api/charges"];
    } else if (path.startsWith("/notifications")) {
        prefixes = ["/api/invoices", "/api/charges"];
    } else if (path.includes("/patient-portal/questionnaire")) {
        prefixes = ["/api/recipients", "/api/patients", "/api/invoices", "/api/dashboard"];
    } else if (path.startsWith("/patient-portal")) {
        prefixes = ["/api/invoices", "/api/charges", "/api/dashboard"];
    } else if (path.startsWith("/upload")) {
        prefixes = ["/api/uploads", "/api/invoices", "/api/patients", "/api/recipients", "/api/exams", "/api/dashboard", "/api/goa"];
    } else if (path.startsWith("/gdt-sync")) {
        prefixes = ["/api/patients", "/api/recipients", "/api/dashboard"];
    } else if (path.startsWith("/settings")) {
        // Several settings affect server-computed charges figures (vatRate, and now the PVS
        // comparison inputs behind /charges/pvs-savings) — without this, a save here leaves
        // stale cached /charges data visible elsewhere (e.g. the dashboard hero card) until
        // the 2-minute TTL happens to expire.
        prefixes = ["/api/settings", "/api/charges"];
    } else if (path.startsWith("/tags")) {
        // Deleting/renaming a tag can change what invoices display (deletion removes it from tagIds).
        prefixes = ["/api/tags", "/api/invoices"];
    } else if (path.startsWith("/groups")) {
        // Renaming changes the label shown everywhere; deleting unassigns the group from patients.
        prefixes = ["/api/groups", "/api/patients", "/api/recipients"];
    } else if (path.startsWith("/banksapi")) {
        prefixes = ["/api/banksapi", "/api/invoices", "/api/dashboard"];
    }

    if (prefixes.length) {
        invalidateCache(...prefixes);
        window.dispatchEvent(new CustomEvent("cache-invalidated", { detail: prefixes }));
    }
}
